import { computed, signal } from '@angular/core';

export interface OverlayPosition {
  top: number;
  left: number;
  width: number;
  height: number;
}

export const overlayPositionOf = (el: Element): OverlayPosition => {
  const r = el.getBoundingClientRect();
  return { top: r.top, left: r.left, width: r.width, height: r.height };
};

// One instance per overlay feature (extend it into a root-provided service), so several overlays can be open at the same time.
// `anchor` belongs to the host page: it registers the element the overlay docks to on view init and clears it on destroy.
// Without an anchor the overlay falls back to covering the main content column, so the left navigation stays usable.
export class EmbeddedUiOverlayState {
  open = signal(false);
  minimized = signal(false);
  maximized = signal(false);
  anchor = signal<HTMLElement | null>(null);
  position = signal<OverlayPosition | null>(null);
  visible = computed(() => this.open() && !this.minimized());
  docked = computed(() => !!this.anchor());

  minimize() {
    this.minimized.set(true);
  }

  restore() {
    this.minimized.set(false);
  }

  maximize() {
    this.maximized.set(true);
  }

  unmaximize() {
    this.maximized.set(false);
  }

  setAnchor(el: HTMLElement | null) {
    this.anchor.set(el);
    this.position.set(el ? overlayPositionOf(el) : null);
  }

  updatePosition(pos: OverlayPosition) {
    this.position.set(pos);
  }

  // `open` goes first so `visible` never flips true mid-reset. The anchor is left alone, it follows the host page, not the overlay.
  reset() {
    this.open.set(false);
    this.minimized.set(false);
    this.maximized.set(false);
  }
}
